/**
 * Consent helpers — GDPR/CCPA posture.
 *
 * The consumer app (apps/marketing, apps/web) is responsible for rendering
 * the consent banner UI. This module only persists and reads the decision.
 *
 * Storage: localStorage under a fixed key, so the decision survives reloads
 * and cross-tab navigation. No cookie — nothing is sent to servers, so no
 * CSRF / third-party concerns.
 *
 * Values: 'granted' | 'revoked' | null (null == no decision yet, behave as
 * revoked until the user chooses).
 *
 * Server-side / non-browser callers (Astro SSR, tests) see `hasConsent()`
 * return false — safest default.
 */

export const CONSENT_STORAGE_KEY = 'roadbridge.analytics.consent';

export type ConsentState = 'granted' | 'revoked';

/** Guard against SSR / non-browser runtimes where `window` is absent. */
function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

/**
 * Returns true only if the user has explicitly granted consent.
 * Returns false in every other case — unset, revoked, or SSR.
 */
export function hasConsent(): boolean {
  if (!isBrowser()) return false;
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY) === 'granted';
  } catch {
    // Storage may be blocked (private mode, iframe sandbox). Default to false.
    return false;
  }
}

/**
 * Reads the raw stored state. Useful for UI that wants to differentiate
 * "not asked yet" from "asked and declined".
 */
export function getConsentState(): ConsentState | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (raw === 'granted' || raw === 'revoked') return raw;
    return null;
  } catch {
    return null;
  }
}

/**
 * Record explicit consent. Call this from your consent-banner "Accept" button.
 * After this, `track()` will actually dispatch to PostHog.
 */
export function grantConsent(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, 'granted');
  } catch {
    // Swallow — nothing we can do if storage is blocked.
  }
}

/**
 * Record explicit revocation. Call this from your consent-banner "Decline"
 * button and from any user-facing "opt out" affordance (e.g. /legal/privacy).
 * Consumers should ALSO call `optOut()` from `./client` after revoking, so
 * PostHog stops firing in the current session.
 */
export function revokeConsent(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, 'revoked');
  } catch {
    // Swallow.
  }
}

/**
 * Wipes the stored decision, returning to "not asked". Mostly used in tests
 * and on account deletion. Not wired into the marketing UI.
 */
export function clearConsent(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Swallow.
  }
}
